import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngxs/store';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import { ModalComponent } from 'src/app/private/shared/components/modals/modal/modal.component';
import { ProformsFormComponent } from 'src/app/private/shared/components/proforms/proforms-form/proforms-form.component';
import {
  PdCreditAwardingMethod,
  PdEarnedItem,
} from 'src/app/private/shared/dtos/pd.dto';
import { ProformViewStates } from 'src/app/private/shared/dtos/proforms.dto';
import { LMSCourseService } from 'src/app/private/shared/services/lms-courses/lms-course.service';
import { PdService } from 'src/app/private/shared/services/pd/pd.service';
import { ProformsSubmissionService } from 'src/app/private/shared/services/proforms/proforms-submission.service';
import { LmsCourseRegistrationAPIResponse } from 'src/app/private/shared/types/responses/lms-course.responses';
import {
  ProFormContext,
  ProFormsSubmissionAPIResponse,
} from 'src/app/private/shared/types/responses/proforms.responses';

@Component({
  selector: 'app-microlearning-action-list',
  templateUrl: './microlearning-action-list.component.html',
  styleUrls: ['./microlearning-action-list.component.scss'],
  standalone: false,
})
export class MicrolearningActionListComponent implements OnInit, OnChanges {
  @Input() courseId: number | undefined;

  @Input() sessionId: number | undefined;

  @Input() quizId: number | undefined;

  @Input() surveyId: number | undefined;

  @Input() isReviewed = false;

  @Input() creditsEarned = false;

  @Input() creditAwardingMethod: PdCreditAwardingMethod | undefined;

  @Output() readonly quizPassed = new EventEmitter<void>();

  @ViewChild('takeQuizModal') takeQuizModal: ModalComponent;

  @ViewChild('takeQuizForm') takeQuizForm: ProformsFormComponent;

  @ViewChild('takeSurveyModal') takeSurveyModal: ModalComponent;

  @ViewChild('gradedQuizModal')
  gradedQuizModal: ModalComponent;

  @ViewChild('viewQuizSubmissionModal') viewQuizSubmissionModal: ModalComponent;

  user: User;

  registration: LmsCourseRegistrationAPIResponse;

  completedSteps: Set<string> = new Set();

  steps: {
    key: string;
    label: string;
    icon: string;
    action: () => void;
  }[] = [];

  viewStates = ProformViewStates;

  contextType = ProFormContext.PD_SESSION;

  gradedFormId: number | null = null;

  gradedSubmissionId: number | null = null;

  userQuizSubmission: ProFormsSubmissionAPIResponse;

  constructor(
    private store: Store,
    private lmsCourseService: LMSCourseService,
    private pdService: PdService,
    private proformsSubmissionService: ProformsSubmissionService
  ) {
    this.user = this.store.selectSnapshot(UserState.getUser) as User;
  }

  ngOnInit() {
    const userId = this.user.id;

    if (this.courseId) {
      this.lmsCourseService
        .getUserCourseRegistration(this.courseId, userId)
        .subscribe((res) => {
          if (res) {
            this.registration = res.item;
          }
          this.buildSteps();
        });
    }
    this.buildSteps();
  }

  ngOnChanges() {
    this.buildSteps();
  }

  buildSteps(): void {
    this.steps = [];

    if (this.courseId && this.registration) {
      this.steps.push({
        key: 'launchCourse',
        label: 'Launch Course',
        icon: 'bi-arrow-up-right-square-fill',
        action: () => this.launchCourse(),
      });
    }

    this.steps.push({
      key: 'takeQuiz',
      label: 'Take Quiz',
      icon: 'bi-card-checklist',
      action: () => this.takeQuiz(),
    });

    if (this.creditAwardingMethod !== PdCreditAwardingMethod.QUIZ_PASSED) {
      this.steps.push({
        key: 'facilitatorReview',
        label: 'Facilitator Review',
        icon: 'bi-binoculars-fill',
        action: () => {},
      });
    }

    this.steps.push({
      key: 'creditsEarned',
      label: 'Credits Earned',
      icon: 'bi-award-fill',
      action: () => {},
    });

    if (this.surveyId) {
      this.steps.push({
        key: 'takeSurvey',
        label: 'Take Survey',
        icon: 'bi-card-text',
        action: () => this.takeSurvey(),
      });
    }

    this.handleSubmissionProgress();
  }

  isStepCompleted(stepKey: string): boolean {
    return this.completedSteps.has(stepKey);
  }

  markStepCompleted(stepKey: string): void {
    this.completedSteps.add(stepKey);
  }

  markStepUncompleted(stepKey: string): void {
    this.completedSteps.delete(stepKey);
  }

  // Add this method to determine when steps are available/clickable
  isStepAvailable(stepKey: string): boolean {
    // If step is already completed, it should not be available for clicking
    if (this.isStepCompleted(stepKey)) {
      return false;
    }

    switch (stepKey) {
      case 'launchCourse':
        // Course launch is available if registration exists and not completed
        return !!this.registration;

      case 'takeQuiz':
        // Quiz is available if course is launched (or no course required) and not completed
        return !this.courseId || this.isStepCompleted('launchCourse');

      case 'facilitatorReview':
        // Facilitator review is available after quiz is taken (if quiz exists) and not completed
        return !this.quizId || this.isStepCompleted('takeQuiz');

      case 'creditsEarned':
        // Credits are never clickable - just a status indicator
        return false;

      case 'takeSurvey': {
        // Survey is available if prerequisites are met and not completed
        const quizCompleted = !this.quizId || this.isStepCompleted('takeQuiz');
        const creditsEarned = this.isStepCompleted('creditsEarned');
        return quizCompleted && creditsEarned;
      }

      default:
        return false;
    }
  }

  handleSubmissionProgress(): void {
    if (this.isReviewed) {
      this.markStepCompleted('facilitatorReview');
    }

    if (this.creditsEarned) {
      this.markStepCompleted('creditsEarned');

      const facilitatorReviewStep = this.steps.find(
        (step) => step.key === 'facilitatorReview'
      );
      if (facilitatorReviewStep) {
        this.markStepCompleted('facilitatorReview');
      }
    } else {
      this.markStepUncompleted('creditsEarned');
    }

    if (this.isStepCompleted('takeQuiz')) {
      this.markStepCompleted('launchCourse');
    }

    if (this.quizId) {
      this.proformsSubmissionService.getAllSubmissions(this.quizId).subscribe({
        next: (resp) => {
          if (resp.items?.length) {
            this.userQuizSubmission = resp.items.find(
              (s: ProFormsSubmissionAPIResponse) => s.user_id === this.user.id
            );

            if (this.userQuizSubmission) {
              this.markStepCompleted('launchCourse');
              if (this.userQuizSubmission?.pass_or_fail === 'pass') {
                this.markStepCompleted('takeQuiz');
              }
            }
          }
        },
      });
    }

    if (this.surveyId) {
      this.proformsSubmissionService
        .getAllSubmissions(this.surveyId)
        .subscribe({
          next: (resp) => {
            if (resp.items?.length) {
              const submission = resp.items.find(
                (s: ProFormsSubmissionAPIResponse) =>
                  s.user_id === this.user.id &&
                  s.context_table_id === this.sessionId
              );
              if (submission) {
                this.markStepCompleted('takeSurvey');
              }
            }
          },
        });
    }
  }

  launchCourse() {
    if (this.registration) {
      window.open(this.registration.launch_link, '_blank');
      this.markStepCompleted('launchCourse');
    }
  }

  viewQuizSubmission() {
    if (this.userQuizSubmission) {
      this.viewQuizSubmissionModal.open();
    }
  }

  takeQuiz() {
    // Reset the submission service to clear any existing data
    this.proformsSubmissionService.resetNewSubmission();
    this.takeQuizForm.resetForm();

    // Create a fresh payload for the quiz
    if (this.quizId && this.contextType && this.sessionId) {
      this.proformsSubmissionService.createPayload(
        this.contextType,
        this.sessionId,
        []
      );
    }

    this.takeQuizModal.open();
  }

  submitQuiz(submission: ProFormsSubmissionAPIResponse) {
    this.proformsSubmissionService.resetNewSubmission();
    if (this.sessionId) {
      this.proformsSubmissionService.createPayload(
        this.contextType,
        this.sessionId,
        []
      );
      if (this.creditAwardingMethod === PdCreditAwardingMethod.QUIZ_PASSED) {
        this.pdService
          .sessionEarnedCredits(this.sessionId)
          .subscribe((resp) => {
            if (resp.items) {
              const currentUsersCredits = resp.items.find(
                (credit: PdEarnedItem) => credit.user.id === this.user.id
              );
              if (currentUsersCredits.credits.length > 0) {
                this.quizPassed.emit();
                this.markStepCompleted('creditsEarned');
              }
            }
          });

        if (submission?.pass_or_fail === 'pass') {
          this.markStepCompleted('launchCourse');
          this.markStepCompleted('takeQuiz');
        }
      }
    }
    this.handleSubmissionProgress();
  }

  openGradedQuizModal(event: { formId: number; submissionId: number }) {
    this.gradedFormId = event.formId;
    this.gradedSubmissionId = event.submissionId;
    this.gradedQuizModal.open();
  }

  takeSurvey() {
    // Reset the submission service to clear any existing quiz data
    this.proformsSubmissionService.resetNewSubmission();

    // Also clear any stored form data for the survey
    if (this.surveyId && this.contextType && this.sessionId) {
      const surveyStorageKey = `proform-${this.surveyId}-submission-payload-${
        this.contextType
      }-${this.sessionId}${this.user ? `-user-${this.user.id}` : ''}`;

      // Remove any cached survey data
      StorageService.removeItem(surveyStorageKey);

      // Create a fresh payload for the survey
      this.proformsSubmissionService.createPayload(
        this.contextType,
        this.sessionId,
        []
      );
    }

    this.takeSurveyModal.open();
  }

  // Add method to handle survey completion
  completeSurvey() {
    this.markStepCompleted('takeSurvey');
    this.takeSurveyModal.close();
  }
}
